/**
 * React Hooks for MQTT Manager
 * 
 * Usage:
 *   import { useMqttClient, useMqttSubscription, useMqttStatus } from './Utils/useMqtt';
 *   
 *   // In your component:
 *   const { client, status, isConnected } = useMqttClient({
 *     clientId: 'my-client',
 *     host: 'ws://broker.example.com:8083/mqtt',
 *   });
 *   
 *   // Subscribe to a topic
 *   const { messages, lastMessage } = useMqttSubscription(client, 'sensors/+/temperature');
 *   
 *   // Global status monitoring
 *   const { clients, stats } = useMqttStatus();
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { MQTTManager, MQTTStatus } from './mqtt';

/**
 * Hook to create and manage an MQTT client
 * @param {object} config - Client configuration
 * @param {string} config.clientId - Unique client identifier
 * @param {string} config.host - WebSocket URL
 * @param {string} [config.username] - Username
 * @param {string} [config.password] - Password
 * @param {boolean} [autoConnect=true] - Whether to connect automatically
 * @returns {object} - { client, status, isConnected, connect, disconnect }
 */
export function useMqttClient(config, autoConnect = true) {
  const [status, setStatus] = useState(null);
  const [client, setClient] = useState(null);
  const configRef = useRef(config);

  // Keep config ref updated
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  const handleStatusChange = useCallback((update) => {
    setStatus(update.status);
  }, []);

  const connect = useCallback(() => {
    if (!configRef.current?.clientId || !configRef.current?.host) {
    // console.error('[useMqttClient] Missing clientId or host in config');
      return null;
    }

    const mqttClient = MQTTManager.createClient(configRef.current, handleStatusChange);
    setClient(mqttClient);
    return mqttClient;
  }, [handleStatusChange]);

  const disconnect = useCallback(() => {
    if (configRef.current?.clientId) {
      MQTTManager.closeClient(configRef.current.clientId);
      setClient(null);
      setStatus(MQTTStatus.CLOSED);
    }
  }, []);

  // Auto connect on mount
  useEffect(() => {
    if (autoConnect && config?.clientId && config?.host) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      // Don't auto-disconnect as other components might use the same client
      // If you want auto-cleanup, call disconnect() explicitly
    };
  }, [autoConnect, config?.clientId, config?.host, connect]);

  return {
    client,
    status,
    isConnected: status === MQTTStatus.CONNECTED,
    connect,
    disconnect,
  };
}

/**
 * Hook to subscribe to an MQTT topic
 * @param {MQTTClient} client - MQTT client instance
 * @param {string} topic - Topic pattern to subscribe
 * @param {object} options - Options
 * @param {boolean} [options.parseJson=true] - Whether to parse messages as JSON
 * @param {number} [options.maxMessages=100] - Maximum messages to keep in history
 * @returns {object} - { messages, lastMessage, error, subscribe, unsubscribe }
 */
export function useMqttSubscription(client, topic, options = {}) {
  const { parseJson = true, maxMessages = 100 } = options;
  
  const [messages, setMessages] = useState([]);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleMessage = useCallback((receivedTopic, message) => {
    let payload = message.toString();
    
    if (parseJson) {
      try {
        payload = JSON.parse(payload);
      } catch {
        // Keep as string if not valid JSON
      }
    }

    const messageObj = {
      topic: receivedTopic,
      payload,
      timestamp: Date.now(),
    };

    setLastMessage(messageObj);
    setMessages((prev) => {
      const newMessages = [messageObj, ...prev];
      return newMessages.slice(0, maxMessages);
    });
  }, [parseJson, maxMessages]);

  const subscribe = useCallback(async () => {
    if (!client || !topic) return false;
    
    try {
      const success = await client.subscribe(topic, handleMessage);
      setIsSubscribed(success);
      setError(null);
      return success;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [client, topic, handleMessage]);

  const unsubscribe = useCallback(async () => {
    if (!client || !topic) return false;
    
    try {
      const success = await client.unsubscribe(topic);
      setIsSubscribed(!success);
      return success;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [client, topic]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setLastMessage(null);
  }, []);

  // Auto subscribe when client is connected
  useEffect(() => {
    if (client?.isConnected() && topic && !isSubscribed) {
      subscribe();
    }
  }, [client, topic, isSubscribed, subscribe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (client && topic && isSubscribed) {
        client.unsubscribe(topic).catch(() => {});
      }
    };
  }, [client, topic, isSubscribed]);

  return {
    messages,
    lastMessage,
    error,
    isSubscribed,
    subscribe,
    unsubscribe,
    clearMessages,
  };
}

/**
 * Hook to publish messages to MQTT
 * @param {MQTTClient} client - MQTT client instance
 * @returns {object} - { publish, isPublishing, error }
 */
export function useMqttPublish(client) {
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState(null);

  const publish = useCallback(async (topic, payload, options = {}) => {
    if (!client) {
      setError('No MQTT client available');
      return false;
    }

    setIsPublishing(true);
    setError(null);

    try {
      const success = await client.publish(topic, payload, options);
      if (!success) {
        setError('Failed to publish: client not connected');
      }
      return success;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setIsPublishing(false);
    }
  }, [client]);

  return {
    publish,
    isPublishing,
    error,
  };
}

/**
 * Hook to monitor global MQTT status
 * @returns {object} - { clients, stats }
 */
export function useMqttStatus() {
  const [clients, setClients] = useState({});
  const [stats, setStats] = useState({});

  useEffect(() => {
    // Initial state
    setClients(MQTTManager.listClients());
    setStats(MQTTManager.getStats());

    // Listen for updates
    const unsubscribe = MQTTManager.addStatusListener((update) => {
      if (update.clients) {
        setClients(update.clients);
      }
      setStats(MQTTManager.getStats());
    });

    return unsubscribe;
  }, []);

  return {
    clients,
    stats,
    getClient: MQTTManager.getClient.bind(MQTTManager),
    closeClient: MQTTManager.closeClient.bind(MQTTManager),
    closeAll: MQTTManager.closeAll.bind(MQTTManager),
  };
}

/**
 * Hook to get or create a shared MQTT client
 * Useful when multiple components need the same connection
 * @param {string} clientId - Client identifier
 * @param {object} config - Connection config (used only for creation)
 * @returns {object} - { client, status, isConnected }
 */
export function useSharedMqttClient(clientId, config = null) {
  const [status, setStatus] = useState(null);
  const [client, setClient] = useState(null);

  useEffect(() => {
    // Try to get existing client first
    let mqttClient = MQTTManager.getClient(clientId);
    
    // Create new if not exists and config is provided
    if (!mqttClient && config) {
      mqttClient = MQTTManager.createClient(
        { ...config, clientId },
        (update) => setStatus(update.status)
      );
    }

    if (mqttClient) {
      setClient(mqttClient);
      setStatus(mqttClient.status);
    }

    // Listen for global status updates
    const unsubscribe = MQTTManager.addStatusListener((update) => {
      if (update.clientId === clientId) {
        setStatus(update.status);
      }
    });

    return unsubscribe;
  }, [clientId, config]);

  return {
    client,
    status,
    isConnected: status === MQTTStatus.CONNECTED,
  };
}

/**
 * Multi-broker MQTT connection hook
 * Manages connections across multiple broker configs with automatic failover
 * 
 * @param {Array<object>} configs - Array of broker configs (without clientId)
 *   Each config: { host, username?, password?, reconnectPeriod? }
 * @param {Function} generateClientId - Callback (config, index) => string to generate client ID
 * @param {object} options - Hook options
 * @param {number} [options.connectionTimeout=10000] - Timeout in ms per connection attempt
 * @param {boolean} [options.autoConnect=true] - Auto-connect on mount
 * @param {Function} [options.onBrokerChange] - Callback when broker switches
 * @returns {object} Hook return value
 */
export function useMultiMqtt(configs, generateClientId, options = {}) {
  const {
    connectionTimeout = 10000,
    autoConnect = true,
    onBrokerChange,
  } = options;

  // State
  const [client, setClient] = useState(null);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [status, setStatus] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Refs for mutable values accessed in callbacks
  const configsRef = useRef(configs);
  const generateClientIdRef = useRef(generateClientId);
  const subscriptionsRef = useRef(new Map()); // Map<topic, callback>
  const currentClientRef = useRef(null);
  const isActiveRef = useRef(true); // Track if hook is still mounted
  const connectionAttemptRef = useRef(null); // Track current connection attempt
  const shouldReconnectRef = useRef(true); // Control reconnection behavior

  // Keep refs updated
  useEffect(() => {
    configsRef.current = configs;
  }, [configs]);

  useEffect(() => {
    generateClientIdRef.current = generateClientId;
  }, [generateClientId]);

  /**
   * Re-subscribe all stored subscriptions to the current client
   */
  const resubscribeAll = useCallback(async (mqttClient) => {
    const subscriptions = subscriptionsRef.current;
    for (const [topic, callback] of subscriptions.entries()) {
      try {
        await mqttClient.subscribe(topic, callback);
        // console.info(`[useMultiMqtt] Re-subscribed to: ${topic}`);
      } catch (err) {
        // console.error(`[useMultiMqtt] Re-subscribe error on ${topic}:`, err);
      }
    }
  }, []);

  /**
   * Close current client if any
   */
  const closeCurrentClient = useCallback(() => {
    if (currentClientRef.current) {
      const clientId = currentClientRef.current.clientId;
      MQTTManager.closeClient(clientId);
      currentClientRef.current = null;
    }
  }, []);

  /**
   * Attempt connection to a specific broker config
   * Returns a promise that resolves when connected or rejects on timeout/error
   */
  const attemptConnection = useCallback(async (config, index) => {
    // Await clientId generation (supports async callbacks)
    const clientId = await generateClientIdRef.current(config, index);
    
    return new Promise((resolve, reject) => {
      const fullConfig = { ...config, clientId, reconnectPeriod: 0 }; // Disable auto-reconnect

      let timeoutId = null;
      let resolved = false;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const handleStatus = (update) => {
        if (resolved) return;

        if (update.status === MQTTStatus.CONNECTED) {
          resolved = true;
          cleanup();
          resolve({ client: MQTTManager.getClient(clientId), config, index });
        } else if (update.status === MQTTStatus.ERROR || update.status === MQTTStatus.CLOSED) {
          resolved = true;
          cleanup();
          MQTTManager.closeClient(clientId);
          reject(new Error(`Connection failed: ${update.error || update.status}`));
        }
      };

      // Set timeout
      timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          MQTTManager.closeClient(clientId);
          reject(new Error('Connection timeout'));
        }
      }, connectionTimeout);

      // Attempt connection
      try {
        MQTTManager.createClient(fullConfig, handleStatus);
      } catch (err) {
        resolved = true;
        cleanup();
        reject(err);
      }
    });
  }, [connectionTimeout]);

  /**
   * Try connecting to brokers sequentially starting from a given index
   */
  const tryConnect = useCallback(async (startIndex = 0) => {
    if (!isActiveRef.current || !shouldReconnectRef.current) return;
    if (configsRef.current.length === 0) {
    // console.warn('[useMultiMqtt] No broker configs provided');
      return;
    }

    setIsConnecting(true);
    closeCurrentClient();

    const totalConfigs = configsRef.current.length;
    let attemptIndex = startIndex;
    let attempts = 0;

    while (attempts < totalConfigs && isActiveRef.current && shouldReconnectRef.current) {
      const config = configsRef.current[attemptIndex];
      // console.info(`[useMultiMqtt] Trying broker ${attemptIndex + 1}/${totalConfigs}: ${config.host}`);
      
      setCurrentIndex(attemptIndex);
      setStatus(MQTTStatus.INITIATED);

      try {
        const result = await attemptConnection(config, attemptIndex);
        
        if (!isActiveRef.current || !shouldReconnectRef.current) {
          // Hook unmounted or disconnected during connection
          MQTTManager.closeClient(result.client.clientId);
          return;
        }

        // Success!
        currentClientRef.current = result.client;
        setClient(result.client);
        setCurrentConfig(result.config);
        setCurrentIndex(result.index);
        setStatus(MQTTStatus.CONNECTED);
        setIsConnecting(false);

        // Notify broker change
        if (onBrokerChange) {
          onBrokerChange(result.config, result.index);
        }

        // Re-subscribe all stored subscriptions
        await resubscribeAll(result.client);

        // Set up disconnect handler to try next broker
        const unsubscribe = MQTTManager.addStatusListener((update) => {
          if (update.clientId === result.client.clientId) {
            setStatus(update.status);

            // On disconnect/error/offline, try next broker
            if (
              isActiveRef.current &&
              shouldReconnectRef.current &&
              (update.status === MQTTStatus.DISCONNECTED ||
               update.status === MQTTStatus.ERROR ||
               update.status === MQTTStatus.OFFLINE ||
               update.status === MQTTStatus.CLOSED)
            ) {
              unsubscribe();
              // console.info(`[useMultiMqtt] Broker disconnected, trying next...`);
              // Try next broker
              const nextIndex = (result.index + 1) % configsRef.current.length;
              tryConnect(nextIndex);
            }
          }
        });

        return; // Connected successfully
      } catch (err) {
        // console.warn(`[useMultiMqtt] Broker ${attemptIndex + 1} failed:`, err.message);
        attempts++;
        attemptIndex = (attemptIndex + 1) % totalConfigs;
      }
    }

    // All brokers failed, cycle back to first after a delay
    if (isActiveRef.current && shouldReconnectRef.current) {
      // console.warn('[useMultiMqtt] All brokers failed, retrying from first in 5s...');
      setStatus(MQTTStatus.OFFLINE);
      setIsConnecting(false);
      
      connectionAttemptRef.current = setTimeout(() => {
        if (isActiveRef.current && shouldReconnectRef.current) {
          tryConnect(0);
        }
      }, 5000);
    }
  }, [attemptConnection, closeCurrentClient, onBrokerChange, resubscribeAll]);

  /**
   * Manual connect trigger
   */
  const connect = useCallback(() => {
    shouldReconnectRef.current = true;
    tryConnect(0);
  }, [tryConnect]);

  /**
   * Disconnect and stop reconnection attempts
   */
  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    
    if (connectionAttemptRef.current) {
      clearTimeout(connectionAttemptRef.current);
      connectionAttemptRef.current = null;
    }
    
    closeCurrentClient();
    setClient(null);
    setCurrentConfig(null);
    setCurrentIndex(-1);
    setStatus(MQTTStatus.CLOSED);
    setIsConnecting(false);
  }, [closeCurrentClient]);

  /**
   * Subscribe to a topic (persists across broker changes)
   */
  const subscribe = useCallback(async (topic, callback) => {
    // Store subscription for persistence
    subscriptionsRef.current.set(topic, callback);

    // If connected, subscribe immediately
    if (currentClientRef.current?.isConnected()) {
      try {
        await currentClientRef.current.subscribe(topic, callback);
        return true;
      } catch (err) {
        // console.error(`[useMultiMqtt] Subscribe error:`, err);
        return false;
      }
    }
    
    // Will be subscribed when connected
    return true;
  }, []);

  /**
   * Unsubscribe from a topic
   */
  const unsubscribe = useCallback(async (topic) => {
    // Remove from stored subscriptions
    subscriptionsRef.current.delete(topic);

    // If connected, unsubscribe immediately
    if (currentClientRef.current?.isConnected()) {
      try {
        await currentClientRef.current.unsubscribe(topic);
        return true;
      } catch (err) {
        // console.error(`[useMultiMqtt] Unsubscribe error:`, err);
        return false;
      }
    }
    
    return true;
  }, []);

  /**
   * Publish a message
   */
  const publish = useCallback(async (topic, payload, pubOptions = {}) => {
    if (!currentClientRef.current?.isConnected()) {
      // console.warn('[useMultiMqtt] Not connected, cannot publish');
      return false;
    }

    try {
      return await currentClientRef.current.publish(topic, payload, pubOptions);
    } catch (err) {
      // console.error(`[useMultiMqtt] Publish error:`, err);
      return false;
    }
  }, []);

  // Auto-connect on mount and reconnect when configs change
  useEffect(() => {
    isActiveRef.current = true;

    if (autoConnect && configs.length > 0) {
      // Close existing connections and reconnect with new configs
      shouldReconnectRef.current = false;
      
      if (connectionAttemptRef.current) {
        clearTimeout(connectionAttemptRef.current);
        connectionAttemptRef.current = null;
      }
      
      closeCurrentClient();
      setClient(null);
      setCurrentConfig(null);
      setCurrentIndex(-1);
      
      // Start fresh connection with new configs
      shouldReconnectRef.current = true;
      tryConnect(0);
    }

    // Cleanup on unmount
    return () => {
      isActiveRef.current = false;
      shouldReconnectRef.current = false;
      
      if (connectionAttemptRef.current) {
        clearTimeout(connectionAttemptRef.current);
      }
      
      closeCurrentClient();
    };
  }, [autoConnect, configs, tryConnect, closeCurrentClient]);

  return {
    client,
    currentConfig,
    currentIndex,
    status,
    isConnected: status === MQTTStatus.CONNECTED,
    isConnecting,
    subscribe,
    unsubscribe,
    publish,
    connect,
    disconnect,
  };
}

export default {// eslint-disable-line
  useMqttClient,
  useMqttSubscription,
  useMqttPublish,
  useMqttStatus,
  useSharedMqttClient,
  useMultiMqtt,
};


